/**
 * Utilitário para conversão de URLs de imagens
 * Suporta: URLs locais, AWS S3, Google Drive
 */

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Extrai o ID do arquivo do Google Drive de uma URL
 * @param {string} url - URL do Google Drive
 * @returns {string|null} - ID do arquivo ou null
 */
export const extractGoogleDriveId = (url) => {
  if (!url || typeof url !== 'string') return null;
  
  // Formato: https://drive.google.com/file/d/FILE_ID/view
  let match = url.match(/drive\.google\.com\/file\/d\/([a-zA-Z0-9_-]+)/);
  if (match && match[1]) return match[1];
  
  // Formato: https://drive.google.com/open?id=FILE_ID
  match = url.match(/drive\.google\.com\/open\?id=([a-zA-Z0-9_-]+)/);
  if (match && match[1]) return match[1];
  
  // Formato: https://drive.google.com/uc?id=FILE_ID
  match = url.match(/drive\.google\.com\/uc\?id=([a-zA-Z0-9_-]+)/);
  if (match && match[1]) return match[1];
  
  // Formato genérico com id=
  if (url.includes('drive.google.com') || url.includes('docs.google.com')) {
    match = url.match(/[?&]id=([a-zA-Z0-9_-]+)/);
    if (match && match[1]) return match[1];
  }
  
  return null;
};

/**
 * Converte URL do Google Drive para URL de imagem direta
 * 
 * @param {string} url - URL original da imagem
 * @returns {string} - URL direta para exibição da imagem
 */
export const getDirectGoogleDriveUrl = (url) => {
  if (!url || typeof url !== 'string') return '';
  
  const fileId = extractGoogleDriveId(url);
  if (fileId) {
    return `https://drive.google.com/thumbnail?id=${fileId}&sz=w1000`;
  }
  
  return url;
};

/**
 * Verifica se uma URL é do Google Drive
 * @param {string} url - URL para verificar
 * @returns {boolean} - true se for URL do Google Drive
 */
export const isGoogleDriveUrl = (url) => {
  if (!url || typeof url !== 'string') return false;
  return url.includes('drive.google.com') || url.includes('docs.google.com');
};

/**
 * Verifica se uma URL é do AWS S3
 * @param {string} url - URL para verificar
 * @returns {boolean} - true se for URL do S3
 */
export const isS3Url = (url) => {
  if (!url || typeof url !== 'string') return false;
  return url.includes('.s3.') && url.includes('amazonaws.com');
};

/**
 * Verifica se uma URL é relativa do backend
 * @param {string} url - URL para verificar
 * @returns {boolean} - true se for URL relativa
 */
export const isRelativeUrl = (url) => {
  if (!url || typeof url !== 'string') return false;
  return url.startsWith('/api/images/') || url.startsWith('/api/anexos/');
};

/**
 * Processa uma URL de imagem, convertendo se necessário
 * Suporta: URLs locais (/api/images/), AWS S3, Google Drive
 * 
 * @param {string} url - URL original
 * @returns {string} - URL processada para exibição
 */
export const processImageUrl = (url) => {
  if (!url || typeof url !== 'string') return '';
  if (url.trim() === '') return '';
  
  // Se é URL relativa do backend (/api/images/...), construir URL completa
  if (isRelativeUrl(url)) {
    return `${API_URL}${url}`;
  }
  
  // Se é URL do S3, manter como está (URLs públicas funcionam direto)
  if (isS3Url(url)) {
    return url;
  }
  
  // Se for do Google Drive, converter
  if (isGoogleDriveUrl(url)) {
    return getDirectGoogleDriveUrl(url);
  }
  
  // Se já é URL absoluta (http/https), retornar como está
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }
  
  // Fallback: assumir que é relativa ao backend
  return `${API_URL}${url}`;
};

/**
 * Gera URL para thumbnail de uma imagem
 * Útil para listas e previews menores
 * 
 * @param {string} url - URL original
 * @param {number} maxWidth - Largura máxima do thumbnail
 * @returns {string} - URL do thumbnail
 */
export const getThumbnailUrl = (url, maxWidth = 300) => {
  if (!url) return '';
  
  // Para Google Drive, usar parâmetro de tamanho
  if (isGoogleDriveUrl(url)) {
    const fileId = extractGoogleDriveId(url);
    if (fileId) {
      return `https://drive.google.com/thumbnail?id=${fileId}&sz=w${maxWidth}`;
    }
  }
  
  // Para outras URLs, processar normalmente
  return processImageUrl(url);
};

export default {
  getDirectGoogleDriveUrl,
  isGoogleDriveUrl,
  isS3Url,
  isRelativeUrl,
  processImageUrl,
  getThumbnailUrl
};
